// ─────────────────────────────────────────────────────────────────────────────
// utils/bildschirmSitzung.ts — EIN ANZEIGE-TOKEN IST KEINE ANMELDUNG AN DER
// VERWALTUNGSOBERFLAECHE (T-M24.2 — REQ-OPS-052 Praez. 1, REQ-SC-050,
// REQ-SEC-012, REQ-OPS-055 (b))
//
// ── WOFUER ──────────────────────────────────────────────────────────────────
// Bildschirm-Ansichten und Verwaltungsoberflaeche liegen am SELBEN Ursprung und
// teilen damit EINE `getAuth`-Instanz samt ihrer Persistenz im `localStorage`
// (`firebase/config.ts`). Eine ueber `screentoken` ausgestellte Sitzung
// (REQ-SC-050) ueberlebt deshalb im Browser — und keine der drei
// Bildschirm-Ansichten beendet sie je selbst (am 2026-08-22 nachgemessen:
// `signOut` kommt in ganz `dashboard/src` an genau EINER Stelle vor, in
// `stores/authStore.ts`).
//
// Trifft der Start der Verwaltungsoberflaeche auf eine solche Sitzung, LIEST er
// heute `users/{uid}` und faellt in `permission-denied`: die Rules trennen den
// Anzeige-Anspruch ueber `isAdminUser()` ausdruecklich ab (REQ-SEC-012,
// `firestore.rules` — `allow read, write: if isAdminUser()`; im Emulator
// gemessen, T-M24.1). Daraus wurde ein `bootstrapError` samt Fehleransicht, und
// „Erneut versuchen" fuehrte zwangslaeufig wieder an dieselbe Stelle.
//
// **REQ-OPS-052 Praez. 1 entscheidet das anders, und der Satz haengt an keiner
// Ursachenannahme:** eine solche Sitzung gilt beim Start als „NICHT ANGEMELDET"
// und nicht als Fehler. Sie wird abgemeldet, die Oberflaeche fuehrt nach
// `/login`. Ein Anspruch, der gar nicht fuer diese Oberflaeche ausgestellt
// wurde, ist kein Fehler, sondern eine falsche Tuer.
//
// ── DAS MERKMAL IST DER ANSPRUCH, NICHT DER FEHLERCODE (REQ-OPS-052 Praez. 1) ─
// Erkannt wird an `getIdTokenResult().claims.screen === true` — an demselben
// Merkmal, das die Rules lesen (`request.auth.token.get('screen', false) ==
// true`). **NICHT am Ausgang des Firestore-Lesevorgangs.** Ein an
// `permission-denied` festgemachter Zweig faenge auch den ECHTEN Rechtefehler
// eines Verwaltungsnutzers und meldete ihn stillschweigend ab — er verdeckte
// damit REQ-SEC-011, die im Kern offen ist. Am 2026-08-22 ist gemessen, dass
// beide Lagen heute buchstaeblich denselben Zustand erzeugen
// (`test/screenTokenPortalStart.test.ts`, Fall 1d) und dass dieselbe
// Benutzerkennung OHNE den Anspruch lesen DARF — es ist der Anspruch und nicht
// die Kennung (Emulator-Beleg des Ausgangstores T-M24.1).
//
// ── DAS TOR: NUR AM START DER VERWALTUNGSOBERFLAECHE ────────────────────────
// ⚠ **Ohne dieses Tor waere der Bau eine Schleife statt einer Behebung.**
// `main.ts` ruft `authStore.init()` auf JEDER Adresse, auch an
// `/kitchen/:printerId` — und dort meldet sich die Ansicht mit genau so einer
// Sitzung an (`composables/useScreenChannel.ts`,
// `signInWithCustomToken(screenAuth, …)` auf der EIGENEN Instanz `screen`). Eine
// Erkennung ohne Ortsbezug meldete die Bildschirm-Ansicht ab, waehrend sie
// laeuft; sie meldete sich neu an, und das Spiel begaenne von vorn.
//
// ⚠ **BERICHTIGT AM 2026-09-08 (T-M34.2, REQ-SC-109; berichtigt statt geloescht,
// REQ-OPS-036 Praez. 6).** Bis dahin stand hier „`signInWithCustomToken(auth, …)`
// auf DERSELBEN Instanz" — das traf zu und trifft seit der Trennung nicht mehr
// zu: die Bildschirme melden sich auf `screenApp`/`screenAuth` an
// (`browserSessionPersistence`), die Verwaltung bleibt auf `[DEFAULT]`.
// **Das Tor bleibt trotzdem stehen und wird NICHT zurueckgebaut**
// (REQ-OPS-052 Praez. 1, REQ-SC-109 (6)): sein Gegenstand ist ab jetzt enger,
// aber wirklich — **Altbestaende im Slot `[DEFAULT]`**, die vor der Trennung
// dort entstanden sind und bis zu ihrer Abmeldung liegen bleiben, und jeder
// kuenftige Fehlgriff, der eine Anzeige-Sitzung wieder auf die Portal-Instanz
// braechte. *Ein Schutz, dessen Ursache beseitigt ist, wird nicht abgebaut,
// sondern in seiner Reichweite benannt.*
//
// Geprueft wird `location.pathname` und NICHT der Router-Zustand: der Store
// laeuft im Bootstrap, VOR der ersten Aufloesung des Routers — dieselbe
// Begruendung und dieselbe EINE Quelle wie bei `utils/legacySw.ts`, deren
// `istBildschirmPfad` hier wiederverwendet und nicht nachgebaut wird (die drei
// Geltungsbereiche stehen in `utils/kitchenPwa.ts`; eine vierte
// Bildschirm-Ansicht erbt das Tor dadurch automatisch).
//
// ── GRENZEN, benannt statt verschwiegen ────────────────────────────────────
//  (i)  **Die Abmeldung wirkt nur noch auf den Slot `[DEFAULT]`** (berichtigt
//       am 2026-09-08, T-M34.2 / REQ-SC-109 (1); berichtigt statt geloescht,
//       REQ-OPS-036 Praez. 6). *Bis zum 2026-09-08 stand hier: „Die Abmeldung
//       wirkt tabuebergreifend. Die Persistenz von Firebase Auth haengt am
//       Ursprung; wer die Verwaltung oeffnet, waehrend im selben Browser ein
//       Kitchen TV laeuft, meldet dessen Sitzung mit ab."* Das traf zu, solange
//       beide dieselbe App-Instanz benutzten. Seit der Trennung liegt die
//       Anzeige-Sitzung auf `screenApp` in `sessionStorage` (je Tab) und wird
//       von dieser Abmeldung nicht mehr erreicht. **Unveraendert gilt der
//       Rueckweg**: verliert eine Ansicht ihren Anspruch — aus welchem Grund
//       auch immer —, meldet sie sich ueber `screentoken` von selbst neu an
//       (REQ-OPS-053 (f), zweite Auspraegung des stillen Falls), ohne Neuladen
//       und ohne Zutun.
//  (ii) **Wer den Anspruch nicht lesen kann, gilt NICHT als Bildschirm.** Wirft
//       `getIdTokenResult()` (Netz, abgelaufener Ausweis) oder fehlt die
//       Methode, ist die Antwort `false`. Die sichere Wahl ist hier die
//       NACHSICHTIGE: ein Verwaltungsnutzer, der wegen eines Netzfehlers
//       abgemeldet wuerde, waere ein neuer Defekt — der bisherige Weg samt
//       Fehlerpfad bleibt fuer ihn unveraendert erhalten.
//  (iii)**Das stellt keine Mandantentrennung her** (REQ-SEC-011 bleibt offen
//       und ist ausdruecklich nicht Gegenstand). Es nimmt eine falsche Tuer
//       weg und oeffnet keine richtige.
// ─────────────────────────────────────────────────────────────────────────────

import { istBildschirmPfad } from '@/utils/legacySw';

/**
 * Der Name des Anspruchs, den `screentoken` ausstellt (REQ-SC-050).
 *
 * **Er steht hier als Konstante, weil er an zwei Orten dasselbe bedeuten muss:**
 * `firebase/functions/src/index.ts` stellt ihn aus (`{ screen: true,
 * screenPrinterId, companyId }`), `firebase/firestore.rules` liest ihn
 * (`request.auth.token.get('screen', false) == true`). Diese Datei ist der
 * dritte Leser und benutzt denselben Namen — kein zweiter.
 */
export const BILDSCHIRM_ANSPRUCH = 'screen';

/**
 * Der Kanal-Anspruch, allein fuer die Protokollzeile (REQ-OPS-055 (b)).
 * Er entscheidet nichts; erkannt wird an {@link BILDSCHIRM_ANSPRUCH}.
 */
export const BILDSCHIRM_KANAL_ANSPRUCH = 'screenPrinterId';

/**
 * Die Sicht auf einen angemeldeten Nutzer, so weit dieses Modul sie braucht.
 *
 * Bewusst ein eigener, MINIMALER Typ und nicht `User`: ein echter Firebase-User
 * erfuellt ihn strukturell, eine Attrappe im Test ebenso — ohne dass der Test
 * die halbe Schnittstelle nachbauen muesste. **`getIdTokenResult` ist optional**,
 * und das ist keine Bequemlichkeit: es haelt den Fall (ii) der Grenzen oben im
 * Typ fest statt in einem Kommentar.
 */
export interface AnspruchsSitzung {
  readonly uid?: string;
  /**
   * **OHNE Parameter, und das ist die Aussage:** die Sicht nimmt kein
   * `forceRefresh` entgegen, weil hier nie eines uebergeben wird (der echte
   * Firebase-`User` erfuellt sie trotzdem — eine Methode mit einem
   * WAHLFREIEN Parameter passt auf eine parameterlose Sicht). Der
   * zwischengespeicherte Ausweis traegt den Anspruch bereits; ein erzwungenes
   * Erneuern machte aus einer oertlichen Pruefung einen Netzvorgang im
   * Startpfad.
   */
  getIdTokenResult?: () => Promise<{
    readonly claims?: Record<string, unknown>;
  } | null | undefined>;
}

/** Was die Erkennung gefunden hat — Grundlage der einen Protokollzeile. */
export interface BildschirmAnspruchsBefund {
  /** Traegt die Sitzung den Anspruch `screen === true`? */
  istBildschirm: boolean;
  /** Die Kanalkennung aus dem Anspruch, sofern lesbar (nur fuer die Diagnose). */
  printerId: string | null;
}

/** Der Befund „kein Anzeige-Token" — die Antwort in jedem Zweifelsfall. */
const KEIN_BILDSCHIRM: BildschirmAnspruchsBefund = { istBildschirm: false, printerId: null };

/**
 * Liest den ANSPRUCH des Ausweises (REQ-OPS-052 Praez. 1).
 *
 * **Genau EIN Aufruf von `getIdTokenResult()` je Aufloesung des
 * Anmeldezustands** — kein Aufruf im Takt (REQ-SC-078 sinngemaess), kein
 * `forceRefresh`: der zwischengespeicherte Ausweis traegt den Anspruch bereits,
 * und ein erzwungenes Erneuern machte aus einer oertlichen Pruefung einen
 * Netzvorgang im Startpfad.
 *
 * **Sie wirft nie** (Grenze (ii)): jeder Fehlschlag ist `KEIN_BILDSCHIRM`.
 */
export async function leseBildschirmAnspruch(
  user: AnspruchsSitzung | null | undefined,
): Promise<BildschirmAnspruchsBefund> {
  if (!user || typeof user.getIdTokenResult !== 'function') return KEIN_BILDSCHIRM;
  try {
    const ergebnis = await user.getIdTokenResult();
    const claims   = ergebnis?.claims;
    if (!claims || typeof claims !== 'object') return KEIN_BILDSCHIRM;
    // STRIKT `=== true`, wie die Rules (`== true`) und nicht „wahrheitswertig":
    // ein `screen: 'false'` waere sonst ein Bildschirm.
    if (claims[BILDSCHIRM_ANSPRUCH] !== true) return KEIN_BILDSCHIRM;
    const kanal = claims[BILDSCHIRM_KANAL_ANSPRUCH];
    return { istBildschirm: true, printerId: typeof kanal === 'string' && kanal ? kanal : null };
  } catch {
    return KEIN_BILDSCHIRM;
  }
}

/**
 * Laeuft dieser Start am Portal — also NICHT in einer der drei
 * Bildschirm-Ansichten?
 *
 * Die Liste der drei Geltungsbereiche kommt aus `utils/kitchenPwa.ts` ueber
 * `utils/legacySw.ts`; hier steht keine zweite.
 */
export function istPortalPfad(pathname: string): boolean {
  return !istBildschirmPfad(pathname ?? '');
}

/** Der aktuelle Pfad, ohne Annahmen ueber die Umgebung. */
function aktuellerPfad(): string {
  return typeof location === 'undefined' ? '' : (location.pathname ?? '');
}

/**
 * **DIE EINE ERKENNUNG** (T-M24.2 Umfang (2)): Trifft der Start der
 * VERWALTUNGSOBERFLAECHE auf eine Sitzung mit Anzeige-Anspruch?
 *
 * Sie liegt an genau dieser Stelle und wird nicht in Store und Waechter doppelt
 * gebaut; ihr einziger Aufrufer ist `stores/authStore.ts`.
 *
 * @param pathname Vorgabe `location.pathname` — im Test gestellt.
 */
export async function erkenneBildschirmSitzungAmPortal(
  user: AnspruchsSitzung | null | undefined,
  pathname: string = aktuellerPfad(),
): Promise<BildschirmAnspruchsBefund> {
  // Das Tor ZUERST: an einer Bildschirm-Adresse wird der Anspruch gar nicht
  // erst gelesen — dort ist er der Normalfall und keine Feststellung wert.
  if (!istPortalPfad(pathname)) return KEIN_BILDSCHIRM;
  return leseBildschirmAnspruch(user);
}
